import { Component, Vue, Watch } from "vue-property-decorator";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import IframePopup from "@/components/popup/IframePopup.vue";
import { GuestbookRules } from "@/models/guestbook/GuestbookRules";
import AddVisitor from "@/views/guestbook/dialogs/AddVisitor.vue";
import ViewSurveyResult from "@/views/guestbook/dialogs/ViewSurveyResult.vue";
import { GuestbookVisitStatus } from "@/enums/GuestbookVisitStatus";
import moment from "moment";
import { ValidationRules } from "@/utils/validation-rules";
import Loader from "@/components/loader/Loader.vue";
import { getFormattedDateTimeString } from "@/utils/dateTimeFormat";

@Component({
  components: {
    ConfirmDlg,
    AddVisitor,
    ViewSurveyResult,
    IframePopup,
    Loader
  }
})
export default class GuestbookView extends Vue {
  viewEvent = {};
  flexHeight = 520;
  editMode = false;
  readonly = true;
  dialog = false;
  startDateMenu = false;
  loading = true;
  timeFormat = ''
  pickers = {
    date: false,
    start: false,
    end: false
  };

  time = {
    date: null,
    start: null,
    end: null
  };

  minDate = moment(new Date()).format("YYYY-MM-DD"); //today
  guestbookVisitStatus = GuestbookVisitStatus;

  isDisabled = false;

  $refs!: {
    confirmDelete: HTMLFormElement;
    SurveyPopup: HTMLFormElement;
  };

  get facility() {
    return this.$store.state.facilityModule.selectedFacility;
  }

  get startDate() {
    return this.guestbookVisit && this.guestbookVisit?.start
      ? moment(this.guestbookVisit.start).format('ddd, ' + this.facility.dateFormat)
      : moment(new Date()).format('ddd, ' + this.facility?.dateFormat);
  }

  get formattedStartTime(){
    if(!this.facility.timeFormat) return ''

    return moment(this.time.start, 'HH:mm').format(this.facility?.timeFormat)
  }

  get formattedEndTime(){
    if(!this.facility.timeFormat) return ''

    return moment(this.time.end, 'HH:mm').format(this.facility?.timeFormat)
  }

  get guestbookVisit() {
    return this.$store.state.guestbookModule.currentGuestbookVisit;
  }

  get guestbookRules(): GuestbookRules {
    return this.$store.state.guestbookModule.guestbookRules;
  }

  get guestbookVisitStart() {
    if (typeof this.guestbookVisit.start !== "string") {
      return this.guestbookVisit.start.toISOString()
    }
    return this.guestbookVisit.start
  }

  set guestbookVisitStart(value) {
    this.guestbookVisit.start = value.toISOString()
  }

  get rules() {
    return {
      start: [ValidationRules.required],
      end: [
        ValidationRules.required,
        () => {
          const start = moment(this.time.start, "h:mma");
          const end = moment(this.time.end, "h:mma");

          return (
            moment(end)?.isAfter(start) || this.$i18n.messages[this.$i18n.locale]['GuestbookView.EventEndDateCannotBeBeforeStartDate']
          );
        }
      ]
    };
  }

  get checkInVisitorsDisable() {

    const surveyCompletedIds = this.guestbookVisit.surveyCompletedIds || []
    /*
    //RES-1087
    //currently we don't have functionality to review survey and check temperature
    return !this.guestbookVisit.visitors.every(visitor => visitor.reviewedSurvey && visitor.tempChecked < 37.3)
    ||*/
    return this.guestbookVisit.status === this.guestbookVisitStatus.CheckedIn
      ||
      this.guestbookVisit.status === this.guestbookVisitStatus.RefusedEntry
      ||
      !this.guestbookVisit.visitors.every(visitor => surveyCompletedIds.indexOf(visitor.id) !== -1)
  }

  get startVisit() {
    return `${this.guestbookRules.start}:00`;
  }

  get endVisit() {
    return `${this.guestbookRules.end}:00`;
  }

  @Watch("time.start")
  onStartDateChange(start, prevStart) {

    if (prevStart != null) {
      this.time.end = moment(start, "h:mma")
        .add(30, "m")
        .format("HH:mm");
    }
  }

  @Watch("time.end")
  onEndDateChange(end) {
    this.time?.start >= end ? this.isDisabled = true : this.isDisabled = false
  }

  async created() {
    await this.$store.dispatch("getGuestbookVisit", this.$route.params.id);
    this.guestbookVisit.start = moment(this.guestbookVisit.start).toISOString();
    this.time.start = moment(this.guestbookVisit.start).format("HH:mm");
    this.time.end = moment(this.guestbookVisit.end).format("HH:mm");
    this.timeFormat = this.facility.timeFormat.includes('A')?'ampm':'24h'
    this.loading = false;
  }

  formattedDateTimeString(date: Date, addYears: boolean, addTime: boolean) {
    if(!this.facility) return ''

    return getFormattedDateTimeString(date, this.facility.dateFormat, addTime?this.facility.timeFormat: null, addYears);
  }

  getAllowedDates(date) {
    return moment(date)?.isSameOrAfter(moment().format("YYYY-MM-DD"));
  }

  setDate(date) {
    const [startHours, startMinutes] = this.time.start.split(":");

    this.guestbookVisit.start = moment(date)
      .set({
        hours: startHours,
        minutes: startMinutes
      })
      .toDate();

    this.startDateMenu = false;
    this.setTime();
  }

  setTime() {
    const [startHours, startMinutes] = this.time.start.split(":");
    const [endHours, endMinutes] = this.time.end.split(":");

    this.guestbookVisit.start = moment(this.guestbookVisit.start)
      .set({
        hours: startHours,
        minutes: startMinutes
      })
      .toDate();

    this.guestbookVisit.end = moment(this.guestbookVisit.end)
      .set({
        hours: endHours,
        minutes: endMinutes
      })
      .toDate();
  }

  async deleteEvent() {
    const result = await this.$refs.confirmDelete.open(
      this.$i18n.messages[this.$i18n.locale]['GuestbookView.CancelVisit'],
      this.$i18n.messages[this.$i18n.locale]['GuestbookView.ThisVisitWillBeCancelledText'],
      true,
      this.$i18n.messages[this.$i18n.locale]['GuestbookView.CancelReasonSharedWithVisitor']
    );

    if (result.agree) {
      await this.$store.dispatch("deleteVisit", {
        id: this.guestbookVisit.id,
        reason: result.message
      });
    }
  }

  async completeSurvey(visitorId) {
    const url =
      window.location.origin +
      "/surveys/" +
      this.guestbookVisit.preVisitSurveyId +
      "/guestbook/" +
      this.guestbookVisit.id +
      "/visitor/" +
      visitorId;

    this.$refs.SurveyPopup.open(this.$i18n.messages[this.$i18n.locale]['GuestbookView.PreVisitSurvey'], url).then((r) => {
      setTimeout(x => {
        this.$store.dispatch("getGuestbookVisit", this.$route.params.id);
      }, 0)
    })
  }

  async checkInVisitors() {
    if (
      await this.$refs.confirmDelete.open(
        this.$i18n.messages[this.$i18n.locale]['GuestbookView.CheckingInVisitors'],
        this.$i18n.messages[this.$i18n.locale]['GuestbookView.SurveyConfirmationText'],
        false,
        ""
      )
    ) {
      await this.$store.dispatch("checkInVisitors", this.guestbookVisit);
    }
  }

  checkIfCheckedInAlready() {
    return !this.guestbookVisit.status;
  }

  async refuseEntry() {
    if (
      await this.$refs.confirmDelete.open(
        this.$i18n.messages[this.$i18n.locale]['GuestbookView.RefuseEntry'],
        this.$i18n.messages[this.$i18n.locale]['GuestbookView.PressOKToRefuseEntryText'],
        false,
        ""
      )
    ) {
      await this.$store.dispatch("refuseEntry", this.guestbookVisit);
    }
  }

  async saveChanges() {
    this.setTime()
    await this.$store.dispatch("updateVisit", {
      guestbookitem: this.guestbookVisit,
      updateTime: true
    });
    this.$router.push("/guestbook");
  }

  removeVisitor(visitor) {
    const index = this.guestbookVisit.visitors.indexOf(visitor);
    if (index >= 0) this.guestbookVisit.visitors.splice(index, 1);

    this.$store.dispatch("updateVisit", {
      guestbookitem: this.guestbookVisit,
      updateTime: false
    });
  }
}
